import Cross from "@assets/icons/cross.svg";
import ExternalLinkIcon from "@assets/icons/external-link.svg";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { useGoBack } from "@core/routing/use-navigate";
import { userManager } from "@core/service-container";
import styled from "@emotion/styled";
import { WordingKey } from "@typings/i18n-typings";
import { Button } from "@ui/components/buttons/button";
import { TertiaryButton } from "@ui/components/buttons/tertiary-button";
import { Page } from "@ui/components/common/page";
import { PageHeader } from "@ui/components/common/page-header";
import { useCountryConfig } from "@ui/pages/patient-details/hooks/use-country-config";
import { sendAnalyticsEventDeleteAccountCTA } from "@ui/pages/settings/settings-analytics";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import _ from "lodash";
import { Checkbox } from "primereact/checkbox";
import React, { useCallback, useMemo, useState } from "react";

export enum LeavingReason {
	OTHER = "OTHER",
	DIFFICULT_USE = "DIFFICULT_USE",
	TIME_CONSUMING = "TIME_CONSUMING",
	NO_PATIENTS = "NO_PATIENTS",
	MISSING_GUIDANCE = "MISSING_GUIDANCE",
	PRIVACY_CONCERNS = "PRIVACY_CONCERNS",
	NO_BENEFIT = "NO_BENEFIT",
	SPAM = "SPAM",
	TECHNICAL_ISSUES = "TECHNICAL_ISSUES",
}

const DeleteReasonWordings = {
	[LeavingReason.OTHER]: "leavingReason.other",
	[LeavingReason.DIFFICULT_USE]: "leavingReason.difficult_use",
	[LeavingReason.TIME_CONSUMING]: "leavingReason.time_consuming",
	[LeavingReason.NO_PATIENTS]: "leavingReason.no_patients",
	[LeavingReason.MISSING_GUIDANCE]: "leavingReason.missing_guidance",
	[LeavingReason.TECHNICAL_ISSUES]: "leavingReason.technical_issues",
	[LeavingReason.SPAM]: "leavingReason.spam",
	[LeavingReason.NO_BENEFIT]: "leavingReason.no_benefit",
	[LeavingReason.PRIVACY_CONCERNS]: "leavingReason.privacy_concerns",
} satisfies Record<LeavingReason, WordingKey>;

const MAX_LEAVING_REASONS = 3;

export const LeavingReasonsPage: React.FC = () => {
	const [selectedReasons, setSelectedReasons] = useState<LeavingReason[]>([]);
	const { format } = useI18n();
	const [consequencesAcknowledged, setConsequencesAcknowledged] = useState(false);
	const goBack = useGoBack();
	const { countryConfig } = useCountryConfig();

	const shuffledDeleteReasons = useMemo(() => {
		const [otherOption, ...reasons] = Object.values(LeavingReason);
		return [..._.shuffle(reasons), otherOption];
	}, []);

	const checkReason = useCallback(
		(reason: LeavingReason) => {
			const reasonChecked = selectedReasons.includes(reason);
			let newSelectedReasons: LeavingReason[];

			if (reasonChecked) {
				newSelectedReasons = selectedReasons.filter(r => r !== reason);
			} else {
				newSelectedReasons = [...selectedReasons, reason];
			}
			setSelectedReasons(newSelectedReasons);
		},
		[selectedReasons],
	);

	const [deleteAccount, loading] = useAsyncOperation(async () => {
		sendAnalyticsEventDeleteAccountCTA();
		await userManager.deleteUser(selectedReasons);
	}, {});

	return (
		<StyledPage>
			<PageHeader title={format("settings.leavingReasons.header.title")} canGoBack />
			<ResponsiveContainer>
				<Card className="p-card">
					<div>
						<p className="p-size-m">{format("settings.leavingReasons.instructions.title")}</p>
						<Instructions className="p-size-s">{format("settings.leavingReasons.instructions.subtitle")}</Instructions>
						<CheckboxesContainer>
						{shuffledDeleteReasons.map(reason => (
							<Reason
								key={reason}
									checked={selectedReasons.includes(reason)}
									disabled={selectedReasons.length >= MAX_LEAVING_REASONS && !selectedReasons.includes(reason)}
									onClick={() => checkReason(reason)}
									label={format(DeleteReasonWordings[reason])}
								/>
							))}
						</CheckboxesContainer>
					</div>
					<Consequences>
						<p>{format("settings.leavingReasons.consequences.title")}</p>
						<Consequence
							label={format("settings.leavingReasons.consequences.one.title")}
							description={format("settings.leavingReasons.consequences.one.description")}
						/>
						<Consequence
							label={format("settings.leavingReasons.consequences.two.title")}
							description={format("settings.leavingReasons.consequences.two.description")}
						/>
						<Consequence
							label={format("settings.leavingReasons.consequences.three.title")}
							description={format("settings.leavingReasons.consequences.three.description")}
						/>
					</Consequences>
					<Reason
						checked={consequencesAcknowledged}
						onClick={() => setConsequencesAcknowledged(!consequencesAcknowledged)}
						label={format("settings.leavingReasons.consequencesAcknowledgement")}
					/>
					<div>
						<DeleteButton
							uiStyle="danger"
							label={format("settings.leavingReasons.deleteCTA")}
							disabled={!consequencesAcknowledged}
							loading={loading}
							onClick={deleteAccount}
						/>
						<Button uiStyle="secondary" label={format("global.dialog.cancel")} onClick={goBack} />
					</div>
				</Card>
				<SideContainer>
					<p className="p-size-m-bold">{format("settings.leavingReasons.helpSection.title")}</p>
					<p className="p-size-s">{format("settings.leavingReasons.helpSection.description")}</p>
					<ExternalLink
						onClick={() => window.open(countryConfig?.faqUrl, "_blank")}
						label={format("settings.leavingReasons.helpSection.helpCenter")}
						icon={<ExternalLinkIcon width={16} height={16} />}
						size="S"
					/>
					<TertiaryButton
						onClick={() => window.open(`mailto:${countryConfig?.customerSupportEmail}`)}
						label={format("global.contactUs")}
						icon={<ExternalLinkIcon width={16} height={16} />}
						size="S"
					/>
				</SideContainer>
			</ResponsiveContainer>
		</StyledPage>
	);
};

const StyledPage = styled(Page)`
	@media ${physioMeTabletBreakpoint} {
		padding: 0;
		max-width: 100%;
	}
`;

const ResponsiveContainer = styled.div`
	${row(40)};
	flex: 1;
	align-self: center;
	width: 1200px;
	max-width: 100%;
	@media ${physioMeMobileBreakpoint} {
		${stack(40)};
	}
`;

const Card = styled.div`
	${stack(32)};
	padding: 40px;
	align-self: stretch;
	flex: 2;

	@media ${physioMeMobileBreakpoint} {
		padding: 16px;
		border-radius: 0;
	}
`;

const CheckboxesContainer = styled.div`
	${stack(8)};
	margin-top: 8px;
`;

const DeleteButton = styled(Button)`
	margin-right: 16px;
`;

const SideContainer = styled.div`
	${stack()};
	flex: 1;
	@media ${physioMeMobileBreakpoint} {
		padding: 0 16px 16px 16px;
	}
`;

const Instructions = styled.p`
	color: ${colors.grey80};
`;

const Consequences = styled.div`
	${stack(16)}
`;

const ExternalLink = styled(TertiaryButton)`
	margin: 8px 0;
`;

const Reason: React.FC<{ label: string; checked: boolean; onClick: () => void; disabled?: boolean }> = ({
	label,
	onClick,
	disabled = false,
	checked,
}) => (
	<CheckboxContainer onClick={onClick} disabled={disabled && !checked}>
		<StyledCheckbox checked={checked} />
		<Label disabled={disabled && !checked}>{label}</Label>
	</CheckboxContainer>
);

const Label = styled.p<{ disabled: boolean }>`
	color: ${({ disabled }) => (disabled ? colors.grey : colors.black)};
`;

const CheckboxContainer = styled.div<{ disabled: boolean }>`
	${row(undefined, undefined, "center")};
	cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
	pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
`;

const StyledCheckbox = styled(Checkbox)`
	margin-right: 1rem;
`;

const Consequence: React.FC<{ label: string; description: string }> = ({ label, description }) => (
	<ConsequenceContainer>
		<div>
			<CrossContainer>
				<Cross width={12} height={12} color={colors.rejection} />
			</CrossContainer>
		</div>
		<ConsequenceContent>
			<p className="p-size-m-bold">{label}</p>
			<p className="p-size-m" style={{ color: colors.grey80 }}>
				{description}
			</p>
		</ConsequenceContent>
	</ConsequenceContainer>
);

const CrossContainer = styled.div`
	background-color: ${colors.rejection25};
	border-radius: 100px;
	height: 20px;
	width: 20px;
	justify-content: center;
	align-items: center;
	display: flex;
	margin-top: 2px;
`;

const ConsequenceContainer = styled.div`
	${row(12)}
`;

const ConsequenceContent = styled.div`
	${stack(0, "center")}
`;
